// Filtros de /condominios com o MESMO layout, as MESMAS classes e as MESMAS cores do filtro
// lateral de /imoveis (renderFilters de ListPiecesProperties.tsx), sem as abas Comprar/Alugar
// e só com os campos que existem para empreendimento: PREÇOS (slider) → MÍNIMO/MÁXIMO →
// CIDADES → BAIRROS → TIPOS → EMPREENDIMENTO (nome).
//
// O componente só altera o `draftFilters` da página (pages/condominios/index.tsx); o
// debounce de 420 ms que reescreve a URL fica lá. No mobile o CONCLUÍDO só fecha o overlay.
// Contrato da URL NÃO muda: nome, cidade, bairro, tipo, preco_min, preco_max, ids.
import Slider from '@mui/material/Slider';
import { motion } from 'framer-motion';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { IoCloseSharp } from 'react-icons/io5';
import Select from 'react-select';
import { customSelectPropertiesStyles } from '../../../../../components/pieces/welcome/WelcomePieces.mocks';
import { district } from '../../../../../services/selectors';
import { normalizaFaixaPreco } from '../../../../../utils/empreendimentoCard';
import { formatSelects, hexToRgba, isColorLight, toPrice } from '../../../../../utils/FunctionsGlobals';
import { filtrarTiposDoSelect } from '../../../../../utils/tiposBuscaOcultos';
import styles from '../../../../imoveis/components/pieces/list/ListPiecesProperties.module.css';
import { coresCondominioAP, estilosSelectCondominioAP } from '../list/ListPiecesCondominio';
import { coresCondominioHz, estilosSelectCondominioHz } from '../list/ListPiecesCondominio';
import { coresCondominioLr, estilosSelectCondominioLr } from '../list/ListPiecesCondominio';
import { coresCondominioMt, estilosSelectCondominioMt } from '../list/ListPiecesCondominio';
import { coresCondominioIn, coresCondominioNt, estilosSelectCondominioIn, estilosSelectCondominioNt } from '../list/ListPiecesCondominio';
import { coresCondominioCp, estilosSelectCondominioCp } from '../list/ListPiecesCondominio';

export type CondominioFilters = {
    nome: string;
    cidade: string;
    bairro: string;
    tipo: string;
    preco_min: string;
    preco_max: string;
};

type FiltrosCondominiosProps = {
    site: any;
    allDataHome: any;
    codsite?: any;
    origin?: any;
    filtros: CondominioFilters;
    onChange: (_patch: Partial<CondominioFilters>) => void;
    // true = overlay do mobile (mostra CONCLUÍDO e o X)
    activeLargeFilter?: boolean;
    onFechar?: () => void;
};

const MIN_DISTANCE = 1000;

const encontraPorLabel = (lista: Array<any>, label: string) => {
    const alvo = String(label || '').trim().toLowerCase();
    if (!alvo) return null;
    return lista.find((item: any) => String(item?.label || '').trim().toLowerCase() === alvo) || null;
};

const FiltrosCondominios: React.FC<FiltrosCondominiosProps> = ({
    site,
    allDataHome,
    codsite,
    origin,
    filtros,
    onChange,
    activeLargeFilter,
    onFechar
}) => {
    // Aurora (topo 10) / Panorama (topo 13): painel = superfície branca do modelo, estilo nas regras
    // [data-modelo] do ListPiecesProperties.module.css; os inline legados somem (legadoAP). Demais: null.
    const coresAP = coresCondominioAP(site);
    // Horizonte (topo 11): painel no idioma do /imoveis do modelo (regras [data-modelo='horizonte'] do
    // ListPiecesProperties.module.css) — texto/acento = --hz-*, select do modelo, sem os inline legados.
    const coresHz = coresCondominioHz(site);
    // Larissa (topo 14): painel de linha fina do modelo (regras [data-modelo='larissa'] do Company.module.css) —
    // texto = --lr-texto da raiz, select do modelo, sem os inline legados.
    const coresLr = coresCondominioLr(site);
    // Metrópole (topo 15): painel BRANCO de borda fina e raio 8 (regras [data-modelo='metropole'] do ListPiecesProperties.module.css) —
    // texto = tinta do modelo, select do modelo, sem os inline legados.
    const coresMt = coresCondominioMt(site);
    // Internacional (topo 16): painel branco em pílulas (regras [data-modelo='internacional'] do Company.module.css).
    const coresIn = coresCondominioIn(site);
    // Norte (topo 17): painel branco de campos RETOS (regras [data-modelo='norte'] do Company.module.css).
    const coresNt = coresCondominioNt(site);
    // Campo (topo 18): painel de campos em PÍLULA de 48px (regras [data-modelo='campo'] do Company.module.css).
    const coresCp = coresCondominioCp(site);
    const legadoAP = <T,>(estilo: T): T | undefined => (coresAP || coresHz || coresLr || coresMt || coresIn || coresNt || coresCp ? undefined : estilo);
    // ESPELHO de ListPiecesProperties.tsx (corLetra1 / corFundo / colorPicker do filtro) — manter em sincronia
    const corLetra1 = coresAP ? coresAP.painelTexto : coresHz ? coresHz.texto : coresLr ? coresLr.texto : coresMt ? coresMt.tinta : coresIn ? coresIn.tinta : coresNt ? coresNt.tinta : coresCp ? coresCp.tinta : isColorLight(site?.cor2) ? (site?.cor11 || '#111827') : (site?.cor2 || '#111827');
    const corFundo = "#FFFFFF";
    const colorPicker = coresAP ? estilosSelectCondominioAP(coresAP) : coresHz ? estilosSelectCondominioHz(coresHz) : coresLr ? estilosSelectCondominioLr(coresLr) : coresMt ? estilosSelectCondominioMt(coresMt) : coresIn ? estilosSelectCondominioIn(coresIn) : coresNt ? estilosSelectCondominioNt(coresNt) : coresCp ? estilosSelectCondominioCp(coresCp) : customSelectPropertiesStyles(corLetra1,corFundo,site?.cor2,corLetra1);
    const marginIconTag = legadoAP({ marginTop: 3 });
    const stylesIconClose = coresAP || coresLr || coresMt || coresIn || coresNt || coresCp ? 'currentColor' : corLetra1;
    const stylesTags2 = legadoAP({
        color: corLetra1,
        border: `2px solid ${corLetra1}`,
    });
    const moeda = site?.moeda_tipo || 'R$';

    // ── PREÇOS ────────────────────────────────────────────────────────────────────────────
    // `?.[0]` porque o /imoveis quebra quando `precos_venda` vem undefined.
    const faixa = useMemo(() => normalizaFaixaPreco(allDataHome?.precos_venda?.[0]), [allDataHome?.precos_venda]);
    const precoMinUrl = filtros.preco_min ? Number(filtros.preco_min) : undefined;
    const precoMaxUrl = filtros.preco_max ? Number(filtros.preco_max) : undefined;
    // Valor da URL fora da faixa do tenant: amplia SÓ a faixa desenhada.
    const faixaDesenhada = useMemo(() => ({
        min: Math.min(faixa.min, precoMinUrl ?? faixa.min),
        max: Math.max(faixa.max, precoMaxUrl ?? faixa.max),
    }), [faixa.min, faixa.max, precoMinUrl, precoMaxUrl]);
    const valorMinUrl = precoMinUrl ?? faixaDesenhada.min;
    const valorMaxUrl = precoMaxUrl ?? faixaDesenhada.max;
    const [sliderValue, setSliderValue] = useState<number[]>([valorMinUrl, valorMaxUrl]);
    const arrastandoRef = useRef(false);

    useEffect(() => {
        if (arrastandoRef.current) return;
        setSliderValue((prev) => (prev[0] === valorMinUrl && prev[1] === valorMaxUrl ? prev : [valorMinUrl, valorMaxUrl]));
    }, [valorMinUrl, valorMaxUrl]);

    const handleChangePrice = (_: Event, newValue: number | number[], activeThumb: number) => {
        if (!Array.isArray(newValue)) return;
        arrastandoRef.current = true;
        let next = newValue;
        if (next[1] - next[0] < MIN_DISTANCE) {
            if (activeThumb === 0) {
                next = [Math.min(next[0], faixaDesenhada.max - MIN_DISTANCE), next[0] + MIN_DISTANCE];
            } else {
                next = [next[1] - MIN_DISTANCE, Math.max(next[1], faixaDesenhada.min + MIN_DISTANCE)];
            }
        }
        setSliderValue(next);
    };

    const handleCommitted = (_: Event | React.SyntheticEvent, newValue: number | number[]) => {
        arrastandoRef.current = false;
        if (!Array.isArray(newValue)) return;
        // OBRIGATÓRIO: polegar no limite da faixa = NÃO mandar preco_min/preco_max. A API faz
        // `COALESCE(preco_max,0) >= preco_min`; mandar sempre o mínimo esconderia todo
        // empreendimento sem preço.
        onChange({
            preco_min: newValue[0] > faixa.min ? String(Math.round(newValue[0])) : '',
            preco_max: newValue[1] < faixa.max ? String(Math.round(newValue[1])) : '',
        });
    };

    // ── CIDADES / BAIRROS ─────────────────────────────────────────────────────────────────
    const listCity: Array<any> = useMemo(() => formatSelects(allDataHome?.cidades) || [], [allDataHome?.cidades]);
    const cidadeDaLista = useMemo(() => encontraPorLabel(listCity, filtros.cidade), [listCity, filtros.cidade]);
    // O valor gravado na URL continua sendo o NOME (compatível com as URLs atuais e com o
    // LIKE da API); cidade de URL antiga que não está na lista vira {label, value} mesmo assim.
    const cityActual = filtros.cidade ? (cidadeDaLista || { label: filtros.cidade, value: filtros.cidade }) : null;
    // Bairros dependem da cidade. Tenant com 1 cidade só: carrega os bairros dela de cara.
    const idCidadeBairros = cidadeDaLista?.value ?? (!filtros.cidade && listCity.length === 1 ? listCity[0]?.value : null);
    const [listDistrict, setListDistrict] = useState<Array<any>>([]);

    useEffect(() => {
        let vivo = true;
        if (!idCidadeBairros) {
            setListDistrict([]);
            return () => { vivo = false; };
        }
        district(Number(idCidadeBairros), origin, codsite)
            .then((res: any) => { if (vivo) setListDistrict(formatSelects(res?.bairros) || []); })
            .catch(() => { if (vivo) setListDistrict([]); });
        return () => { vivo = false; };
    }, [idCidadeBairros, origin, codsite]);

    const districtActual = filtros.bairro
        ? (encontraPorLabel(listDistrict, filtros.bairro) || { label: filtros.bairro, value: filtros.bairro })
        : null;

    // ── TIPOS ─────────────────────────────────────────────────────────────────────────────
    const listType: Array<any> = useMemo(
        () => filtrarTiposDoSelect(formatSelects(allDataHome?.tipos), codsite),
        [allDataHome?.tipos, codsite]
    );
    const typeActual = filtros.tipo
        ? (listType.find((tipo: any) => String(tipo?.value) === String(filtros.tipo)) || null)
        : null;

    const renderTag = (label: string, aria: string, onRemove: () => void) => (
        <div className={styles.adjust_tags_filter}>
            <button
                aria-label={`Botão para remover filtro ${aria} ${label}`}
                title={`Clique para remover filtro ${aria} ${label}`}
                type="button"
                className={styles.tags_picker}
                onClick={onRemove}
                style={stylesTags2}
            >
                {label}
                <IoCloseSharp
                    size={16}
                    color={stylesIconClose}
                    style={marginIconTag}
                />
            </button>
        </div>
    );

    return (
        <motion.div
            key="topo"
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className={styles.container_filter}
            data-nosnippet
            style={legadoAP({background: corFundo,border: `1px solid ${hexToRgba(corLetra1,0.5)}`})}
        >
            <div className={styles.adjust_slider}>
                <div className={styles.adjust_select_price}>
                    <label className={styles.title_select} style={{color: corLetra1}}>
                        PREÇOS
                    </label>
                    <div className={styles.adjust_prices}>
                        <label className={styles.price} style={{color: corLetra1}}>
                            {`${moeda} ${toPrice(sliderValue[0] || 0, 2)}`}
                        </label>
                        <label className={styles.price} style={{color: corLetra1}}>
                            {`${moeda} ${toPrice(sliderValue[1] || 0, 2)}`}
                        </label>
                    </div>
                    {faixa.max > 0 ? (
                        <Slider
                            value={sliderValue}
                            onChange={handleChangePrice}
                            onChangeCommitted={handleCommitted}
                            disableSwap
                            min={faixaDesenhada.min}
                            max={faixaDesenhada.max}
                            step={1000}
                            style={coresAP ? { color: coresAP.painelAcento } : coresHz ? { color: coresHz.acento } : coresLr ? { color: coresLr.texto } : coresMt ? { color: coresMt.acento } : coresIn ? { color: coresIn.base } : coresNt ? { color: coresNt.base } : coresCp ? { color: coresCp.profundo } : {
                                // ESPELHO de ListPiecesProperties.tsx (cor do Slider) — manter em sincronia
                                color: isColorLight(site?.cor5) ? isColorLight(site?.cor2) ? corLetra1 : site?.cor2 : corLetra1,
                            }}
                        />
                    ) : null}
                </div>
            </div>
            <div className={styles.adjust_select_p}>
                <div className={styles.adjust_select_price1} style={{margin: "0px 2.5% 0px 0px"}}>
                    <label className={styles.title_select} style={{color: corLetra1}}>
                        MÍNIMO
                    </label>
                    <input
                        className={styles.input}
                        placeholder="Valor mínimo"
                        onChange={(e) => onChange({ preco_min: e.target.value.replace(/\D/g, '') })}
                        inputMode='numeric'
                        value={filtros.preco_min}
                        style={legadoAP({ color: corLetra1,border: `2px solid ${corLetra1}` })}
                    />
                </div>
                <div className={styles.adjust_select_price1} style={{margin: "0px 0px 0px 2.5%"}}>
                    <label className={styles.title_select} style={{color: corLetra1}}>
                        MÁXIMO
                    </label>
                    <input
                        className={styles.input}
                        placeholder="Valor máximo"
                        onChange={(e) => onChange({ preco_max: e.target.value.replace(/\D/g, '') })}
                        inputMode='numeric'
                        value={filtros.preco_max}
                        style={legadoAP({ color: corLetra1,border: `2px solid ${corLetra1}` })}
                    />
                </div>
            </div>
            <div className={styles.adjust_select}>
                <label className={styles.title_select} style={{color: corLetra1}}>
                    CIDADES
                </label>
                <Select
                    options={listCity}
                    className={styles.select}
                    placeholder="Cidades"
                    noOptionsMessage={() => 'Cidade não encontrada!'}
                    styles={colorPicker}
                    value={cityActual}
                    // Trocar/limpar a cidade limpa o bairro (a lista de bairros é dela).
                    onChange={(opcao: any) => onChange({ cidade: opcao?.label || '', bairro: '' })}
                    instanceId="filtro-condominios-cidade"
                />
            </div>
            {cityActual ? renderTag(cityActual.label, 'da cidade', () => onChange({ cidade: '', bairro: '' })) : null}
            <div className={styles.adjust_select}>
                <label className={styles.title_select} style={{color: corLetra1}}>
                    BAIRROS
                </label>
                <Select
                    options={listDistrict}
                    className={styles.select}
                    placeholder={idCidadeBairros ? 'Bairros' : 'Escolha uma cidade'}
                    noOptionsMessage={() => (idCidadeBairros ? 'Bairro não encontrado!' : 'Escolha uma cidade')}
                    styles={colorPicker}
                    value={districtActual}
                    onChange={(opcao: any) => onChange({ bairro: opcao?.label || '' })}
                    instanceId="filtro-condominios-bairro"
                />
            </div>
            {districtActual ? renderTag(districtActual.label, 'do bairro', () => onChange({ bairro: '' })) : null}
            <div className={styles.adjust_select}>
                <label className={styles.title_select} style={{color: corLetra1}}>TIPOS</label>
                <Select
                    options={listType}
                    className={styles.select}
                    placeholder="Tipos"
                    noOptionsMessage={() => 'Tipo não encontrado!'}
                    styles={colorPicker}
                    value={typeActual}
                    onChange={(opcao: any) => onChange({ tipo: opcao?.value != null ? String(opcao.value) : '' })}
                    instanceId="filtro-condominios-tipo"
                />
            </div>
            {typeActual ? renderTag(typeActual.label, 'do tipo', () => onChange({ tipo: '' })) : null}
            <div className={styles.adjust_select}>
                <label className={styles.title_select} style={{color: corLetra1}}>
                    EMPREENDIMENTO
                </label>
                <input
                    className={styles.input}
                    placeholder="Nome do empreendimento"
                    onChange={(e) => onChange({ nome: e.target.value })}
                    value={filtros.nome}
                    style={legadoAP({ color: corLetra1,border: `2px solid ${corLetra1}` })}
                />
            </div>
            {activeLargeFilter ? (
                <button
                    aria-label={`Botão para concluir busca`}
                    title={`Clique para concluir busca`}
                    type="button"
                    onClick={onFechar}
                    className={styles.button_concluded}
                    // ESPELHO de ListPiecesProperties.tsx (botão CONCLUÍDO) — manter em sincronia
                    style={legadoAP(isColorLight(site?.cor2) ? {background: site?.cor2,color: site?.cor11} : {background: site?.cor2,color: site?.cor13})}
                >
                    CONCLUÍDO
                </button>
            ) : null}
            {activeLargeFilter ? (
                <button
                    aria-label={`Botão para fechar busca personalizada`}
                    title={`Clique para fechar busca personalizada`}
                    type="button"
                    onClick={onFechar}
                    className={styles.closed_modal}
                >
                    <IoCloseSharp
                        size={24}
                        color={stylesIconClose}
                        style={marginIconTag}
                    />
                </button>
            ) : null}
        </motion.div>
    );
};

export default FiltrosCondominios;

// Arquivo dentro de pages/: não é rota (mesmo padrão dos irmãos em pages/condominios/components/**).
export const getServerSideProps = async () => ({ notFound: true });
